import { useState, useEffect, useCallback } from 'react';
import { clientApi } from '../api.js';

// Two switches on this screen do different things, and an admin will reach for the wrong one
// unless the difference is stated where they are. Status is "we have lost this client, take them
// out of everything"; the checkboxes are "we still work with them, just not in that tool".
const STATUS_NOTE =
  'A disabled client is hidden from every tool at once — nothing is deleted, and re-activating ' +
  'brings it all back. To remove a client from one tool only, use the checkboxes below.';

function ModuleChecks({ modules, value, onToggle, busyId, disabled }) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {modules.map(m => (
        <label key={m.id} className="o-check">
          <input
            type="checkbox"
            checked={!!value[m.id]}
            // Only the row being checked is disabled, not the whole set: the probe is one
            // indexed count and blocking the others would make the form feel broken.
            // `disabled` is the separate, whole-set case: the client is inactive, so no
            // per-tool answer here means anything yet.
            disabled={disabled || busyId === m.id}
            onChange={e => onToggle(m, e.target.checked)}
          />
          {m.label}
          {busyId === m.id && <span className="o-check-sub">Checking…</span>}
        </label>
      ))}
    </div>
  );
}

// The dependent-data guard. Unchecking hides a client from a tool, and from inside that tool
// afterwards "hidden" and "never had any" look identical — so the person doing it is told which
// one they are creating, in that tool's own units.
//
// Only for unchecking. Re-checking reveals records rather than hiding them, and a global disable
// is a blanket action whose scope no per-module count could usefully describe.
function ConfirmUncheck({ clientName, module: mod, info, onCancel, onConfirm }) {
  const items = info?.items || [];
  return (
    <div className="o-scrim" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="o-modal" role="dialog" aria-modal="true" style={{ maxWidth: 460 }}>
        <div className="o-modal-head">
          <h2>Remove {clientName} from {mod.label}?</h2>
        </div>

        <div className="o-modal-body">
          {info?.error ? (
            <p style={{ margin: 0, fontSize: 'var(--t-small)' }}>
              Existing records in {mod.label} could not be counted: {info.error}
            </p>
          ) : items.length > 0 ? (
            <>
              <p style={{ margin: 0, fontSize: 'var(--t-small)' }}>
                {clientName} has records in {mod.label}:
              </p>
              <table className="o-tbl">
                <tbody>
                  {items.map(it => (
                    <tr key={it.label}>
                      <td>{it.label}</td>
                      <td className="o-acts">
                        <span className="o-num">{it.capped ? `${it.count}+` : it.count}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 'var(--t-small)' }}>
              {mod.label} keeps no records this page can count.
            </p>
          )}

          <p className="o-hint" style={{ margin: 0 }}>
            <b>Nothing is deleted.</b> The client stops appearing in {mod.label} and its records
            stay in the database exactly as they are. Re-checking the box brings them back.
            The change is applied when you save.
          </p>
        </div>

        <div className="o-modal-foot">
          <button className="o-btn" onClick={onCancel}>Cancel</button>
          <button className="o-btn o-btn-primary" onClick={onConfirm} autoFocus>
            Remove from {mod.label}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewClientForm({ modules, onCreated, onCancel }) {
  const [name,    setName]    = useState('');
  const [checks,  setChecks]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState(null);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true); setErr(null);
    try {
      const d = await clientApi.createClient({ name: name.trim(), modules: checks });
      onCreated(d.client);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="o-panel" style={{ padding: 24, maxWidth: 520 }}>
      <h1 className="o-title" style={{ fontSize: '1.25rem' }}>New client</h1>
      <p className="o-sub" style={{ marginBottom: 24 }}>
        The country belongs in the name — each country is its own client.
      </p>

      <label className="o-field-lbl" htmlFor="nc-name">Client name</label>
      <input id="nc-name" className="o-control" autoFocus value={name}
        placeholder="e.g. Acme UAE"
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') create(); if (e.key === 'Escape') onCancel(); }} />

      <div style={{ marginTop: 24 }}>
        <span className="o-field-lbl">Available in</span>
        {/* No dependent-data guard here: a client that does not exist yet has nothing to
            leave behind in any tool. */}
        <ModuleChecks modules={modules} value={checks}
          onToggle={(m, on) => setChecks(c => ({ ...c, [m.id]: on }))} />
        <p className="o-hint" style={{ marginTop: 10 }}>
          <b>Nothing is selected by default.</b> The client will not appear in any tool until
          you enable one.
        </p>
      </div>

      {err && <p className="o-state o-error" style={{ padding: '12px 0', textAlign: 'left' }}>{err}</p>}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="o-btn o-btn-primary" onClick={create} disabled={saving || !name.trim()}>
          {saving ? 'Creating…' : 'Create client'}
        </button>
        <button className="o-btn o-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function ClientDetail({ client, modules, onSaved }) {
  const [name,   setName]   = useState(client.name);
  const [active, setActive] = useState(client.is_active);
  const [checks, setChecks] = useState(client.modules || {});
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [err,    setErr]    = useState(null);
  const [probing, setProbing] = useState(null);   // module id whose records are being counted
  const [confirm, setConfirm] = useState(null);   // { module, info } awaiting an answer

  useEffect(() => {
    setName(client.name);
    setActive(client.is_active);
    setChecks(client.modules || {});
    setErr(null); setSaved(false);
    setProbing(null); setConfirm(null);
  }, [client.id]);

  const toggleModule = async (m, on) => {
    // Turning ON reveals records rather than hiding them. So does re-checking a box that is
    // still on in the SAVED state — the client never stopped appearing there, so there is
    // nothing to warn about and no reason to spend a request finding out.
    if (on || !client.modules?.[m.id]) {
      setChecks(c => ({ ...c, [m.id]: on }));
      return;
    }
    setProbing(m.id);
    try {
      const info = await clientApi.dependents(client.id, m.id);
      // A tool with no records to lose does not need a confirmation. `checked: false` is not
      // that case — it means nothing here could count them, so ask anyway.
      if (info.checked && info.total === 0) setChecks(c => ({ ...c, [m.id]: false }));
      else setConfirm({ module: m, info });
    } catch (e) {
      // Failing to count is not evidence of nothing. Confirm with the reason shown rather than
      // proceeding quietly or blocking the admin from a change they are entitled to make.
      setConfirm({ module: m, info: { error: e.message } });
    } finally { setProbing(null); }
  };

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      // Identity and enablement are separate endpoints because they are separate permissions:
      // editing a name is editor-level, switching a client out of a tool is admin-level.
      if (name.trim() !== client.name || active !== client.is_active) {
        await clientApi.updateClient(client.id, { name: name.trim(), is_active: active });
      }
      await clientApi.setModules(client.id, checks);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const enabledCount = modules.filter(m => checks[m.id]).length;
  // Spread over the current map rather than rebuilding it: a client may carry a key for a
  // module this page did not list, and dropping it would silently switch that tool off.
  const selectAll = () =>
    setChecks(c => modules.reduce((acc, m) => ({ ...acc, [m.id]: true }), { ...c }));

  return (
    <div style={{ padding: '32px 32px 80px', maxWidth: 560 }}>
      <label className="o-field-lbl" htmlFor="cd-name">Client name</label>
      <input id="cd-name" className="o-control" value={name} onChange={e => setName(e.target.value)} />

      <div style={{ marginTop: 24 }}>
        <span className="o-field-lbl">Status</span>
        <div className="o-toggle">
          {[['Active', true], ['Disabled', false]].map(([label, val]) => (
            <button key={label} aria-selected={active === val} onClick={() => setActive(val)}>
              {label}
            </button>
          ))}
        </div>
        <p className="o-note" style={{ marginTop: 8 }}>{STATUS_NOTE}</p>
      </div>

      <div style={{ marginTop: 8 }}>
        <div className="o-head-row" style={{ marginBottom: 6 }}>
          <span className="o-field-lbl" style={{ margin: 0 }}>Available in</span>
          {/* Only "select all". Turning tools on reveals records and needs no warning, so it is
              safe to do in bulk; turning them off is what the per-module guard exists for, and
              a "clear all" would either fire that guard four times or skip it four times. */}
          <button className="o-btn o-btn-sm o-btn-ghost" onClick={selectAll}
            disabled={!active || enabledCount === modules.length}>
            Select all
          </button>
        </div>
        <ModuleChecks modules={modules} value={checks} onToggle={toggleModule}
          busyId={probing} disabled={!active} />
        {!active && (
          <p className="o-hint" style={{ marginTop: 10 }}>
            <b>This client is disabled.</b> These boxes are kept as they are, but nothing below
            takes effect until the client is active again.
          </p>
        )}
        {active && enabledCount === 0 && (
          <p className="o-hint" style={{ marginTop: 10 }}>
            <b>No tools enabled.</b> This client will not appear anywhere until you enable one.
          </p>
        )}
      </div>

      {err && <p className="o-state o-error" style={{ padding: '12px 0', textAlign: 'left' }}>{err}</p>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 24 }}>
        <button className="o-btn o-btn-primary" onClick={save} disabled={saving || !name.trim()}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <span style={{ fontSize: '.8125rem', color: 'var(--valid)' }}>Saved</span>}
      </div>

      {confirm && (
        <ConfirmUncheck
          clientName={client.name}
          module={confirm.module}
          info={confirm.info}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            setChecks(c => ({ ...c, [confirm.module.id]: false }));
            setConfirm(null);
          }}
        />
      )}
    </div>
  );
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [modules, setModules] = useState([]);
  const [selId,   setSelId]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async (keepId) => {
    try {
      const d = await clientApi.listClients();
      setClients(d.clients || []);
      setModules(d.modules || []);
      if (keepId) setSelId(keepId);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = clients.find(c => c.id === selId) || null;

  return (
    <div className="o-split">
      <aside className="o-side">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span className="o-eyebrow">Clients</span>
          <button className="o-btn o-btn-sm o-btn-primary"
            onClick={() => { setCreating(true); setSelId(null); }}>New</button>
        </div>

        {loading && <div className="o-state">Loading…</div>}
        {!loading && clients.length === 0 && (
          <p className="o-hint"><b>No clients yet.</b> Create one to get started.</p>
        )}

        {clients.map(c => {
          const on = modules.filter(m => c.modules?.[m.id]).length;
          return (
            <button key={c.id} className="o-side-item"
              aria-current={selId === c.id ? 'true' : undefined}
              onClick={() => { setSelId(c.id); setCreating(false); }}>
              <span style={{ opacity: c.is_active ? 1 : 0.5 }}>{c.name || 'Unnamed'}</span>
              {/* The module count is meaningless while the client is disabled — every one of
                  those tools is hiding it regardless. Say which state it is in instead.
                  An active client in zero tools is invisible too, but for a different reason
                  and with a different fix, so it reads as a count, not as "Disabled". */}
              {!c.is_active
                ? <span className="o-badge o-err">Disabled</span>
                : <span className={on === 0 ? 'o-badge' : 'o-badge o-grey'}>
                    {on}/{modules.length}
                  </span>}
            </button>
          );
        })}
      </aside>

      <main>
        {err && <div className="o-state o-error">{err}</div>}

        {creating ? (
          <div style={{ padding: 32 }}>
            <NewClientForm
              modules={modules}
              onCancel={() => setCreating(false)}
              onCreated={(c) => { setCreating(false); load(c.id); }}
            />
          </div>
        ) : selected ? (
          <ClientDetail key={selected.id} client={selected} modules={modules}
            onSaved={() => load(selected.id)} />
        ) : !loading && (
          <div className="o-state">Select a client, or create a new one</div>
        )}
      </main>
    </div>
  );
}
